/**
 * Structured data (JSON-LD) for public group detail pages.
 * Emits schema.org Organization plus BreadcrumbList aligned with `PageBreadcrumb` on the route.
 */

import { env } from '@web/src/lib/env.client'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import {
  plainTextFromRichContent,
  SEO_META_DESCRIPTION_MAX_CHARS,
  truncateMetaDescription,
} from '@web/src/lib/seo/seo-common'
import { absoluteUrlForPath, baseUrl } from '@web/src/lib/utilsClient'
import type { GroupQuery } from 'types/graphql'

const TRAILING_SLASH_RE = /\/$/

function normalizeBaseUrl(u: string) {
  return u.replace(TRAILING_SLASH_RE, '')
}

type GroupForJsonLd = NonNullable<GroupQuery['group']>

function schemaGroupKindPhrase(groupType: GroupForJsonLd['groupType']): string {
  switch (groupType) {
    case 'PUBLIC':
      return 'public lifestyle community group'
    case 'PRIVATE':
      return 'private members-only lifestyle group'
    default: {
      const _exhaustive: never = groupType
      return _exhaustive
    }
  }
}

function groupTypeSegmentForSeoTitle(groupType: GroupForJsonLd['groupType']): string {
  switch (groupType) {
    case 'PUBLIC':
      return 'Public Group'
    case 'PRIVATE':
      return 'Private Group'
    default: {
      const _exhaustive: never = groupType
      return _exhaustive
    }
  }
}

/**
 * Meta description and JSON-LD `description`: unique per group; name + type + optional member count,
 * then tagline or plain-text excerpt from the long description.
 */
export function buildGroupDescription(group: GroupForJsonLd): string {
  const name = group.name?.trim() || 'Group'
  const kind = schemaGroupKindPhrase(group.groupType)
  const members = group.memberCount > 0 ? ` It has ${group.memberCount} members.` : ''
  const tagLine = group.tagLine?.trim()
  const bodyPlain = plainTextFromRichContent(group.description ?? undefined)

  const base = `${name} is a ${kind}.${members}`.trim()
  const detail = (tagLine || bodyPlain).trim()
  const out = detail ? `${base} ${detail}` : base

  if (out.length <= SEO_META_DESCRIPTION_MAX_CHARS) return out

  if (detail) {
    const separatorLen = 1
    const budget = SEO_META_DESCRIPTION_MAX_CHARS - base.length - separatorLen
    if (budget >= 40) {
      const truncatedDetail = truncateMetaDescription(detail, budget)
      return `${base} ${truncatedDetail}`
    }
  }

  return truncateMetaDescription(out, SEO_META_DESCRIPTION_MAX_CHARS)
}

/**
 * BreadcrumbList aligned with `PageBreadcrumb` on the group detail route:
 * Home → Groups → current group.
 */
function buildGroupBreadcrumbListJsonLd(groupName: string, groupPageUrl: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      { '@type': 'ListItem', position: 2, name: 'Discover Groups', item: absoluteUrlForPath('/group/list') },
      { '@type': 'ListItem', position: 3, name: groupName, item: groupPageUrl },
    ],
  }
}

function buildGroupFounderJsonLd(owner: NonNullable<GroupForJsonLd['owner']>): Record<string, unknown> {
  const profileUrl = absoluteUrlForPath('/auth/signup')
  const imageUrl = owner.profileMedia?.[0]?.imageUrlMedium
  const name = owner.name?.trim() || 'Group organizer'

  return {
    '@type': 'Person',
    name,
    url: profileUrl,
    ...(imageUrl ? { image: imageUrl } : {}),
  }
}

export function buildGroupPageJsonLd(input: { group: GroupForJsonLd; description: string }): Record<string, unknown>[] {
  const { group, description: groupDescription } = input

  const pagePath = `/group/${group.slug ?? group.id}`
  const url = `${baseUrl()}${pagePath}`
  const entityId = `${url}#group`

  const trimmedDescription = groupDescription.trim()
  const founder = buildGroupFounderJsonLd(group.owner)

  const coverImage = group.coverImageUrlMedium?.trim() || `${normalizeBaseUrl(baseUrl())}/missing-group.webp`

  const mainEntity: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@id': entityId,
    '@type': 'Organization',
    name: group.name,
    ...(trimmedDescription ? { description: trimmedDescription } : {}),
    url,
    image: coverImage,
    founder,
    ...(group.tags &&
      group.tags.length > 0 && {
        keywords: group.tags.map((tag) => tag.name).join(', '),
      }),
  }

  const breadcrumbList = buildGroupBreadcrumbListJsonLd(group.name.trim() || 'Group', url)

  return [mainEntity, breadcrumbList]
}

/** Loader fields used for `<head>` SEO on the group detail route. */
export type GroupPageSeoLoaderInput = {
  group?: GroupQuery['group'] | null
}

export function groupPageHeadFromLoaderData(loaderData: GroupPageSeoLoaderInput | null | undefined) {
  const group = loaderData?.group ?? undefined

  if (!group) {
    return seo({ title: `Group | ${env.VITE_APP_NAME}` })
  }

  const groupDescription = buildGroupDescription(group)
  const jsonLdData = buildGroupPageJsonLd({ group, description: groupDescription })

  const groupTitleName = group.name?.trim() || 'Group'
  const typeTitle = groupTypeSegmentForSeoTitle(group.groupType)

  return seo({
    title: `${groupTitleName} | ${typeTitle} | ${env.VITE_APP_NAME}`,
    description: groupDescription,
    author: group.owner?.name,
    url: `${baseUrl()}/group/${group.slug ?? group.id}`,
    image: group.coverImageUrlMedium || `${normalizeBaseUrl(baseUrl())}/missing-group.webp`,
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd: jsonLdData,
  })
}
